import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Button,
    FormGroup, FormText,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from "reactstrap";
import { importPackingList, listPackingList, previewPackingList, resetPreviewPackingList, reviewPackingList, updatePackingList, updatePackingListStep } from "../../../actions/PackingList/actions";
import useConfig from "../../../actions/Tenants/config/configHook";
import { toast } from "../../../utils/toast";
import MetaForm, { metaFormIsValid } from "../../Forms/MetaForm";
import WrappedPreviewPackingList from "./PreviewPackingList";

import useEnrichPreviewHook from "./useEnrichPreviewHook";

import { FormField } from "../../../actions/Tenants/config/constantsTyped";
import { isFilledArray } from "../../../utils";

interface PackingListUploadModalProps {
    isOpen: boolean;
    toggle: () => void;
}
export const STEPS = {
    PICK_FILE_AND_REVIEW_CONTEXT: 0,
    REVIEW_CHILDS_AND_IMPORT: 1,
};

export default function PackingListUploadModal({ isOpen, toggle }: PackingListUploadModalProps) {
    const [selectedFile, setSelectedFile] = useState<File | undefined>();
    const [isFilePicked, setIsFilePicked] = useState(false);
    const existing_layers = useSelector((state: any) => state.packingLists.existing_layers);
    const growers_to_insert = useSelector((state: any) => state.packingLists.growers_to_insert);
    const isLoading = useSelector((state: any) => state.packingLists.isLoading);
    const isUpating = useSelector((state: any) => state.packingLists.isUpdating);
    const step = useSelector((state: any) => state.packingLists.step);
    const list_id = useSelector((state: any) => state.packingLists.list_id);
    const current = useSelector((state: any) => state.packingLists.current);
    const dispatch = useDispatch();
    const config = useConfig();
    const [query, setQuery] = useState({ supplier: "", country: "", fruit_type: "", container_no: "", template: "default", parent_label_field: "", child_label_field: "" });
    const import_data = useEnrichPreviewHook();

    const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(resetPreviewPackingList() as any);
        if (event.target.files && event.target.files.length > 0) {

            const file = event.target.files[0];
            // Validate file type
            const allowedFileTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel", "application/vnd.oasis.opendocument.spreadsheet", "application/vnd.ms-excel.sheet.macroEnabled.12", "text/csv"];

            if (!allowedFileTypes.includes(file.type)) {
                toast.error("Invalid file type. Please select a CSV, XLSX, or XLS file.");
                return;
            }

            setSelectedFile(file);
            setIsFilePicked(true);
        }
    };

    const cancel = () => {
        dispatch(listPackingList({ limit: 25, offset: 0 }) as any);
        dispatch(resetPreviewPackingList() as any);
        setSelectedFile(undefined);
        setIsFilePicked(false);
        toggle();
    };

    useEffect(() => {
        setQuery(current);
    }, [current?.id]);

    const getParentLabelFieldOptions = () => {
        if (!import_data) {
            return ["container_no", "purchase_order_number", "receipt", "order_number", "voyage_number", "packing_list_number", "vessel_number"].map((i) => ({ value: i, label: i }));
        }
        return import_data.meta.filter((i) => i.value).map((i) => ({ value: i.key, label: `${i.value} [${i.key}]` }));
    };

    const getChildLabelFieldOptions = () => {
        if (!import_data || !isFilledArray(import_data.children)) {
            return ["supplier_pallet_number", "license_plate"].map((i) => ({ value: i, label: i }));
        }
        return import_data.children[0].meta.filter((i) => i.value).map((i) => ({ value: i.key, label: `${i.value} [${i.key}]` }));
    };


    const childLabelFieldForm: FormField = {
        label: "Preferred pallet label field",
        name: "child_label_field",
        type: "single-select",
        default_value: config.root_config?.default_child_label_field || "supplier_pallet_number",
        options: getChildLabelFieldOptions(),
        mandatory: true,
    };


    const form: FormField[] = [

        {
            label: "Fruit",
            name: "fruit_type",
            type: "select-managed",
            mandatory: true,
        },
        {
            label: "Country",
            name: "country",
            type: "select-managed",
            mandatory: true,
        },
        {
            label: "Supplier",
            name: "supplier",
            type: "select-managed",
            onchange_callback: ({ setValue, field, value }) => {
                setValue(field, value);
                setValue("template", "default");
            },
            mandatory: true,
        },
        {
            label: "Template",
            name: "template",
            type: "select-managed",
            hide_when_no_options: true
        },
        {
            label: "Preferred label field",
            name: "parent_label_field",
            type: "single-select",
            default_value: config.root_config?.default_parent_label_field || "container_no",
            options: getParentLabelFieldOptions(),
            mandatory: true,
        },
        childLabelFieldForm,
    ];


    const handlePreview = () => {
        if (!selectedFile) {
            return;
        }

        if (!metaFormIsValid(form, query)) {
            return;
        }
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("template", query.template);
        formData.append("country", query.country);
        formData.append("supplier", query.supplier);
        formData.append("container_no", query.container_no);
        formData.append("fruit_type", query.fruit_type);
        formData.append("parent_label_field", query.parent_label_field);
        formData.append("child_label_field", query.child_label_field);
        dispatch(previewPackingList(formData) as any);
    };

    const onDebounce = (field, value) => {
        if (!import_data) {
            return;
        }
        setQuery((prev) => {
            const data = { ...prev, [field]: value };
            dispatch(updatePackingList({
                id: list_id,
                ...data,
            }) as any).then((response) => {
                if (!response.error) {
                    dispatch(reviewPackingList(list_id) as any);
                }
            });
            return data;
        });

    };

    const handleImport = () => {

        dispatch(importPackingList(list_id, { import_data, growers_to_insert }) as any).then((response) => {
            if (!response.error) {
                dispatch(listPackingList({ limit: 25, offset: 0 }) as any);
                cancel();
            }
        });
    };
    const isThereGrowersToInsert = growers_to_insert && growers_to_insert.length > 0;
    const onNext = () => {
        dispatch(updatePackingListStep(STEPS.REVIEW_CHILDS_AND_IMPORT));
    };
    const onBack = () => {
        if (step === STEPS.REVIEW_CHILDS_AND_IMPORT && isThereGrowersToInsert) {
            return dispatch(updatePackingListStep(STEPS.PICK_FILE_AND_REVIEW_CONTEXT));
        }
        return dispatch(updatePackingListStep(STEPS.PICK_FILE_AND_REVIEW_CONTEXT));
    };
    const importableState = import_data && isFilePicked && step === STEPS.REVIEW_CHILDS_AND_IMPORT;

    return (
        <Modal fullscreen={true} isOpen={isOpen} toggle={() => cancel()} >
            <ModalHeader toggle={() => cancel()} >Upload packing list</ModalHeader>
            <ModalBody>
                {existing_layers !== false && <Alert color="info">Some pallets / orders are already imported. You can not import this file again.</Alert>}

                {(step === STEPS.PICK_FILE_AND_REVIEW_CONTEXT) && <div>
                    {(!import_data || true) && <MetaForm
                        meta={form}
                        setValue={(field, value) => setQuery((prev) => ({ ...prev, [field]: value }))}
                        object={query || {}}
                        config={config}
                        debounce_time={1200}
                        onDebounce={onDebounce}
                    />}
                    <FormGroup>
                        <Label for="exampleFile">Excel sheet</Label>
                        <Input
                            id="exampleFile"
                            name="file"
                            type="file"
                            accept=".csv,.xlsx,.xlsm,.xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.oasis.opendocument.spreadsheet, application/vnd.ms-excel.sheet.macroEnabled.12"
                            onChange={changeHandler}
                        />

                        {isFilePicked ? (
                            <FormText >
                                <div className="pt-2">
                                    <div>Filename: {selectedFile?.name}</div>
                                    <div>Filetype: {selectedFile?.type}</div>
                                    <div>Size in bytes: {selectedFile?.size}</div>
                                </div>
                            </FormText>
                        ) : (
                            <FormText>
                                Upload file
                            </FormText>
                        )}
                    </FormGroup>
                </div>}

                {(step === STEPS.REVIEW_CHILDS_AND_IMPORT) && <div>
                    <MetaForm
                        meta={[childLabelFieldForm]}
                        setValue={(field, value) => setQuery((query) => ({ ...query, [field]: value }))}
                        object={query || {}}
                        config={config}
                        onDebounce={onDebounce}
                        debounce_time={1200}
                    />
                </div>}

                {(import_data || isUpating) && <WrappedPreviewPackingList />}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={() => cancel()}>Cancel</Button>
                {step === STEPS.PICK_FILE_AND_REVIEW_CONTEXT && !import_data && <Button color="primary" disabled={!isFilePicked || isLoading || isUpating} onClick={() => handlePreview()}>Preview</Button>}
                {(step === STEPS.REVIEW_CHILDS_AND_IMPORT) && <Button color="light" onClick={onBack}>Back</Button>}
                {(step === STEPS.PICK_FILE_AND_REVIEW_CONTEXT) && import_data && <Button color="primary" disabled={!isFilePicked || isLoading || isUpating || !import_data} onClick={onNext}>Next</Button>}
                {importableState && <Button color="warning" disabled={!isFilePicked || isLoading || isUpating || existing_layers !== false} onClick={() => handleImport()}>Import</Button>}
            </ModalFooter>
        </Modal>
    );
}


